import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { $httpRootApi } from '@core/http-client';
import { RequirementShowItem } from '@shared/apis/requirementsApi.types';
import { ViewingTourSummary } from '@shared/models/requirement/viewingTour';

import {
  AcquisitionEmailShortlistPayload,
  AcquisitionPublishToSocietyPayload,
  AcquisitionRecirculateOnSocietyPayload,
  AcquisitionUpdatePayload,
  AcquisitionUpdateRequirementListsPayload,
  AcquisitionViewingTourCreateOrUpdatePayload,
  AcquisitionViewingTourDestroyPayload,
  AcquisitionViewingTourDestroyResponse,
  AvailabilityScheduleMatchAcceptPayload,
  AvailabilityScheduleMatchAddMessagePayload,
  AvailabilityScheduleMatchAddMessageResponse,
  AvailabilityScheduleMatchClearDiscountedReasonPayload,
  AvailabilityScheduleMatchCommentStorePayload,
  AvailabilityScheduleMatchCommentStoreResponse,
  AvailabilityScheduleMatchDeleteMessagePayload,
  AvailabilityScheduleMatchDeleteMessageResponse,
  AvailabilityScheduleMatchMovePayload,
  AvailabilityScheduleMatchUpdatePayload,
  RequirementAvailabilityClearDiscountedReasonPayload,
  RequirementAvailabilityDiscountPayload,
  RequirementAvailabilityDragDropMoveGroupPayload,
  RequirementAvailabilityDragDropMoveGroupResponse,
  RequirementAvailabilityMovePayload,
  RequirementAvailabilityShowItem,
  RequirementAvailabilityUpdatePayload,
} from './acquisitionsApi.types';

export const acquisitionMutations = {
  update: async (payload: AcquisitionUpdatePayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.put<AcquisitionUpdatePayload['body'], AxiosResponse<RequirementShowItem>>(
      `/acquisitions/${payload.id}`,
      payload.body,
      config
    ),

  updateRequirementLists: async (payload: AcquisitionUpdateRequirementListsPayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.put<AcquisitionUpdateRequirementListsPayload['body'], AxiosResponse<RequirementShowItem>>(
      `/acquisitions/${payload.id}/requirement-lists`,
      payload.body,
      config
    ),

  publishToSociety: async (payload: AcquisitionPublishToSocietyPayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.put<AcquisitionPublishToSocietyPayload['body'], AxiosResponse<RequirementShowItem>>(
      `/acquisitions/${payload.id}/publish`,
      payload.body,
      config
    ),

  recirculateOnSociety: async (payload: AcquisitionRecirculateOnSocietyPayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.post<AcquisitionRecirculateOnSocietyPayload['body'], AxiosResponse<RequirementShowItem>>(
      `/acquisitions/${payload.id}/recirculate/${payload.advertId}`,
      payload.body,
      config
    ),

  availabilityScheduleMatchUpdate: async (
    payload: AvailabilityScheduleMatchUpdatePayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.put<
      AvailabilityScheduleMatchUpdatePayload['body'],
      AxiosResponse<RequirementAvailabilityShowItem>
    >(`/acquisitions/availability-schedule/${payload.matchId}`, payload.body, config),

  availabilityScheduleMatchCommentStore: async (
    payload: AvailabilityScheduleMatchCommentStorePayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.post<
      AvailabilityScheduleMatchCommentStorePayload['body'],
      AxiosResponse<AvailabilityScheduleMatchCommentStoreResponse>
    >(`/acquisitions/availability-schedule/${payload.matchId}/comments`, payload.body, config),

  availabilityScheduleMatchAddMessage: async (
    payload: AvailabilityScheduleMatchAddMessagePayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.post<
      AvailabilityScheduleMatchAddMessagePayload['body'],
      AxiosResponse<AvailabilityScheduleMatchAddMessageResponse>
    >(`/acquisitions/availability-schedule/${payload.matchId}/messages`, payload.body, config),

  availabilityScheduleMatchDeleteMessage: async (
    payload: AvailabilityScheduleMatchDeleteMessagePayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.delete<undefined, AxiosResponse<AvailabilityScheduleMatchDeleteMessageResponse>>(
      `/acquisitions/availability-schedule/${payload.matchId}/messages/${payload.messageId}`,
      config
    ),

  availabilityScheduleMatchAccept: async (
    payload: AvailabilityScheduleMatchAcceptPayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.put<
      AvailabilityScheduleMatchAcceptPayload['body'],
      AxiosResponse<RequirementAvailabilityShowItem>
    >(`/acquisitions/availability-schedule/${payload.matchId}/accept`, payload.body, config),

  availabilityScheduleMatchMove: async (payload: AvailabilityScheduleMatchMovePayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.put<
      AvailabilityScheduleMatchMovePayload['body'],
      AxiosResponse<RequirementAvailabilityShowItem>
    >(`/acquisitions/availability-schedule/${payload.matchId}`, payload.body, config),

  availabilityScheduleMatchClearDiscountedReason: async (
    payload: AvailabilityScheduleMatchClearDiscountedReasonPayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.put<undefined, AxiosResponse<RequirementAvailabilityShowItem>>(
      `/acquisitions/availability-schedule/${payload.matchId}/clear-discounted-reason`,
      undefined,
      config
    ),

  emailShortlist: async (payload: AcquisitionEmailShortlistPayload, config?: AxiosRequestConfig) =>
    $httpRootApi.post<AcquisitionEmailShortlistPayload['body'], AxiosResponse<RequirementShowItem>>(
      `/acquisitions/${payload.acquisitionId}/shortlist/email`,
      payload.body,
      config
    ),

  viewingTourCreateOrUpdate: async (
    payload: AcquisitionViewingTourCreateOrUpdatePayload,
    config?: AxiosRequestConfig
  ) =>
    $httpRootApi.post<AcquisitionViewingTourCreateOrUpdatePayload['body'], AxiosResponse<ViewingTourSummary>>(
      `/acquisitions/${payload.acquisitionId}/tour`,
      payload.body,
      config
    ),

  viewingTourDestroy: async (payload: AcquisitionViewingTourDestroyPayload, config?: AxiosRequestConfig) =>
    $httpRootApi.delete<undefined, AxiosResponse<AcquisitionViewingTourDestroyResponse>>(
      `/acquisitions/${payload.acquisitionId}/tour`,
      config
    ),

  availabilityDragDropMoveGroup: async (
    payload: RequirementAvailabilityDragDropMoveGroupPayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.put<
      RequirementAvailabilityDragDropMoveGroupPayload['body'],
      AxiosResponse<RequirementAvailabilityDragDropMoveGroupResponse>
    >(
      `/acquisitions/${payload.acquisitionId}/availability-schedule/${payload.matchId}/drag-drop-move-group`,
      payload.body,
      config
    ),

  availabilityMove: async (payload: RequirementAvailabilityMovePayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.put<RequirementAvailabilityMovePayload['body'], AxiosResponse<RequirementAvailabilityShowItem>>(
      `/acquisitions/availability-schedule/${payload.matchId}`,
      payload.body,
      config
    ),

  availabilityUpdate: async (payload: RequirementAvailabilityUpdatePayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.put<
      RequirementAvailabilityUpdatePayload['body'],
      AxiosResponse<RequirementAvailabilityShowItem>
    >(`/acquisitions/availability-schedule/${payload.matchId}`, payload.body, config),

  availabilityDiscount: async (payload: RequirementAvailabilityDiscountPayload, config?: AxiosRequestConfig) =>
    await $httpRootApi.put<
      RequirementAvailabilityDiscountPayload['body'],
      AxiosResponse<RequirementAvailabilityShowItem>
    >(`/acquisitions/availability-schedule/${payload.matchId}/discount`, payload.body, config),

  availabilityClearDiscountedReason: async (
    payload: RequirementAvailabilityClearDiscountedReasonPayload,
    config?: AxiosRequestConfig
  ) =>
    await $httpRootApi.put<undefined, AxiosResponse<RequirementAvailabilityShowItem>>(
      `/acquisitions/availability-schedule/${payload.matchId}/clear-discounted-reason`,
      undefined,
      config
    ),
};
